// AboutSelah — shared informational block used on the Welcome and Register
// screens. Explains what Selah Downloads IS, what it IS NOT, and WHY it
// exists, so users understand the product before signing up.

import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Heart, Sparkles, BookOpen, Clock } from 'lucide-react';

const IS_ITEMS = [
  {
    icon: Sparkles,
    text: 'A place to capture sudden spiritual insights ("downloads") the moment they arrive',
  },
  {
    icon: BookOpen,
    text: 'AI-guided reflection that turns raw moments into 3 insights, Scripture, and an activation prayer',
  },
  {
    icon: Clock,
    text: 'A journey tracker that watches patterns and confirms how God is moving across your life',
  },
  {
    icon: Heart,
    text: 'A private, encrypted space — your words stay yours',
  },
];

const IS_NOT_ITEMS = [
  'A replacement for prayer, Scripture reading, or church community',
  'A prophetic oracle or substitute for discernment — always weigh insights biblically',
  'Professional mental-health, legal, or financial counseling',
  'A public social feed — entries are private to you',
];

const WHY_ITEMS = [
  {
    title: 'Don\'t lose the spark',
    body: 'Revelation often comes in fleeting moments — a shower, a drive, mid-prayer. Selah captures it before it slips away.',
  },
  {
    title: 'Move from impression to action',
    body: 'Every download becomes structured guidance: insights, a verse, and a prayer you can pray today.',
  },
  {
    title: 'See the pattern',
    body: 'Follow-ups build a timeline. Over weeks, Selah Downloads quietly surfaces what God keeps highlighting so you can confirm it and walk in it.',
  },
];

export const AboutSelah = ({ variant = 'full' }) => {
  // "compact" variant is used on Register; "full" on Welcome.
  const compact = variant === 'compact';

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={compact ? 'space-y-4' : 'space-y-10'}
      data-testid="about-selah"
    >
      {/* WHY */}
      <div>
        <h2
          className={`font-serif font-bold text-[var(--sd-primary-dark)] ${
            compact ? 'text-xl mb-3' : 'text-3xl mb-6 text-center'
          }`}
        >
          Why Selah Downloads?
        </h2>
        <div
          className={
            compact
              ? 'space-y-2'
              : 'grid grid-cols-1 md:grid-cols-3 gap-4'
          }
        >
          {WHY_ITEMS.map((item, i) => (
            <div
              key={i}
              className={`rounded-xl border border-[rgba(184,120,132,0.25)] bg-white/70 p-4 ${
                compact ? '' : 'shadow-sm'
              }`}
            >
              <h3
                className={`font-semibold text-[var(--sd-primary-dark)] ${
                  compact ? 'text-sm mb-1' : 'text-base mb-2'
                }`}
              >
                {item.title}
              </h3>
              <p
                className={`text-[var(--sd-fg-secondary)] leading-relaxed ${
                  compact ? 'text-xs' : 'text-sm'
                }`}
              >
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* IS / IS NOT */}
      <div className={compact ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
        {/* What it IS */}
        <div
          className="rounded-xl border border-[#7C8F7A]/35 bg-[rgba(124,143,122,0.08)] p-5"
          data-testid="about-selah-is"
        >
          <h3
            className={`flex items-center gap-2 font-semibold text-[#3F5238] ${
              compact ? 'text-sm mb-2' : 'text-lg mb-4'
            }`}
          >
            <CheckCircle2
              size={compact ? 16 : 20}
              className="text-[#5C7857] flex-shrink-0"
            />
            What Selah Downloads IS
          </h3>
          <ul className={compact ? 'space-y-1.5' : 'space-y-3'}>
            {IS_ITEMS.map((item, i) => {
              const Icon = item.icon;
              return (
                <li
                  key={i}
                  className={`flex items-start gap-2 text-[var(--sd-fg-secondary)] ${
                    compact ? 'text-xs' : 'text-sm'
                  }`}
                >
                  <Icon
                    size={compact ? 13 : 16}
                    className="text-[var(--sd-primary)] flex-shrink-0 mt-0.5"
                  />
                  <span>{item.text}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* What it IS NOT */}
        <div
          className="rounded-xl border border-[#B87884]/35 bg-[rgba(184,120,132,0.08)] p-5"
          data-testid="about-selah-is-not"
        >
          <h3
            className={`flex items-center gap-2 font-semibold text-[var(--sd-primary-dark)] ${
              compact ? 'text-sm mb-2' : 'text-lg mb-4'
            }`}
          >
            <XCircle
              size={compact ? 16 : 20}
              className="text-[var(--sd-primary-dark)] flex-shrink-0"
            />
            What Selah Downloads IS NOT
          </h3>
          <ul className={compact ? 'space-y-1.5' : 'space-y-3'}>
            {IS_NOT_ITEMS.map((text, i) => (
              <li
                key={i}
                className={`flex items-start gap-2 text-[var(--sd-fg-secondary)] ${
                  compact ? 'text-xs' : 'text-sm'
                }`}
              >
                <XCircle
                  size={compact ? 13 : 16}
                  className="text-[#C8736A] flex-shrink-0 mt-0.5 opacity-70"
                />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.section>
  );
};

export default AboutSelah;
